/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/jsx-props-no-spreading -- `sortableThProps` concentra
   className/onClick/title do cabeçalho ordenável; espalhar aqui é o que evita
   repetir as três props em oito colunas (mesmo padrão do TransferOrderList). */
import React, {
   useCallback,
   useEffect,
   useMemo,
   useRef,
   useState,
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
   FaArrowDown,
   FaArrowUp,
   FaBoxOpen,
   FaDownload,
   FaSearch,
} from 'react-icons/fa';

import Loading from '../../components/Loading';
import PaginationBar from '../../components/PaginationBar/PaginationBar';
import SectionMeta, { SectionHeaderRow } from '../../components/SectionMeta';
import StatusBadge from '../../components/StatusBadge';
import UserNameSearch, {
   userOptionLabel,
} from '../../components/UserNameSearch';
import ProductNameSearch from '../../components/ProductNameSearch';
import usePermissions from '../../components/Menu/MenuItems/hooks/usePermissions';
import { useUserPdvs } from '../../hooks/useUserPdvs';
import { searchMovements } from '../../services/movements/movementsApi';
import { notifyApiError } from '../../utils/feedback';
import {
   formatItemsSummary,
   movementDateParts,
   movementDateSortValue,
   movementFlagLabel,
   movementFlagVariant,
   movementPdvLabel,
   movementUserLabel,
   moveTypeLabel,
   moveTypeVariant,
} from '../../utils/movementFormat';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderActions,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../styles/layout/Section';
import { FormGrid } from '../../styles/layout/FormGrid';
import { Field } from '../../styles/forms/Field';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { DataTable } from '../../styles/ui/Table';
import { EmptyState } from '../../styles/feedback/EmptyState';
import { ClickableRow } from '../../styles/ui/Link';
import {
   CtaButton,
   GhostButton,
   GhostOnDarkButton,
   SmallButton,
} from '../../styles/ui/Button';

import {
   DEFAULT_PER_PAGE,
   MOVE_TYPE_SEGMENTS,
   PERIOD_SHORTCUTS,
   PER_PAGE_OPTIONS,
   SORTABLE_KEYS,
   STATUS_FILTER_LABEL,
   STATUS_SEGMENTS,
} from './constants';
import {
   Chip,
   ChipList,
   FilterFoot,
   FilterHeadRow,
   HeadLeft,
   ResultsTools,
   Segmented,
   ShortcutsGroup,
   TransferTag,
   TwoLine,
} from './styled';

// ===========================================================================
// Consultar movimentos (`POST /movement-search`).
//
// Camada de navegação conforme `navegacao.md` cap.1 desde 2026-08-12
// (`e82bb20`) e PRESERVADA no redesenho de 2026-09-12: URL é a fonte da
// verdade, busca só no submit/ordenação/paginação, sentinela `applied=1`,
// `replace: true` no histórico e sem fetch no mount com URL limpa.
//
// Redesenho visual (handoff de design "Movimentos de Mercadorias"):
//   · header escuro com contadores e atalhos para Nova entrada / Nova saída;
//   · card de Pesquisa com atalhos de período, segmentados de Tipo/Situação e
//     chips do que está aplicado (o × refaz a busca sem aquele filtro);
//   · resultados na tabela do kit novo (`styles/ui/Table`), Documento e Data
//     em duas linhas, ações de linha (ver/imprimir) e seletor de linhas;
//   · dois estados vazios com cópia distinta (não pesquisei × não achei).
//
// Coluna "Itens" (`3 itens · 9 un.`): entrou em 2026-09-12 com o handoff 0109
// do backend, que passou a devolver `items_count`/`units_total` no header do
// movimento — agregados do MOVIMENTO INTEIRO, inclusive quando a busca filtra
// por produto. Fica FORA de `SORTABLE_KEYS`: a ordenação desta tela é
// client-side, e ordenar por agregado é conversa para quando ela for do
// servidor. ⚠️ O backend do 0109 ainda não estava em produção quando esta tela
// foi escrita; sem os campos, a célula mostra "—" (aditivo, sem ordem de
// deploy).
//
// Ausência deliberada do protótipo, por falta de contrato:
//   · filtro "Pendentes" (estado que não existe no ENUM do backend).
//
// O "Exportar" gera CSV NO CLIENTE, a partir das linhas que o filtro já
// trouxe — não há endpoint de export para este recurso.
// ===========================================================================

// Data de hoje (fuso do browser) no formato YYYY-MM-DD dos inputs type=date.
function getTodayIso() {
   return new Date()
      .toLocaleDateString('pt-BR', {
         year: 'numeric',
         month: '2-digit',
         day: '2-digit',
      })
      .split('/')
      .reverse()
      .join('-');
}

// Data de N dias atrás no mesmo formato (N=0 → hoje).
function getIsoDaysAgo(days) {
   const date = new Date();
   date.setDate(date.getDate() - days);
   return date
      .toLocaleDateString('pt-BR', {
         year: 'numeric',
         month: '2-digit',
         day: '2-digit',
      })
      .split('/')
      .reverse()
      .join('-');
}

// Qual atalho de período o par de datas representa. Editar uma data à mão cai
// em 'custom' sem estado extra (e sem param novo na URL).
function derivePeriodKey(startDate, endDate) {
   const today = getTodayIso();
   if (!startDate || !endDate || endDate !== today) return 'custom';

   const match = PERIOD_SHORTCUTS.find(
      (shortcut) => startDate === getIsoDaysAgo(shortcut.days - 1)
   );
   return match ? match.key : 'custom';
}

// Filtros default. Chaves snake_case = nomes dos query params da URL = nomes
// do backend (navegacao.md 1.1), evitando camada de tradução na página.
// As datas defaultam pra HOJE (UX de sempre da tela — e o backend exige o
// intervalo), então "Limpar" nunca deixa a validação de datas quebrada.
function buildDefaultFilters() {
   const today = getTodayIso();
   return {
      start_date: today,
      end_date: today,
      id: '',
      doc_number: '',
      pdv_id: '',
      user_id: '',
      move_type: '',
      movement_flag: '',
      prod_sku: '',
   };
}

function readFiltersFromUrl(searchParams, defaults) {
   const next = { ...defaults };
   Object.keys(defaults).forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null && value !== '') next[key] = value;
   });
   return next;
}

function readSortFromUrl(searchParams) {
   const orderBy = searchParams.get('order_by');
   if (orderBy && SORTABLE_KEYS.has(orderBy)) {
      return {
         key: orderBy,
         direction: searchParams.get('order_dir') === 'desc' ? 'desc' : 'asc',
      };
   }
   return { key: null, direction: null };
}

function readPerPageFromUrl(searchParams) {
   const fromUrl = Number(searchParams.get('per_page'));
   return PER_PAGE_OPTIONS.includes(fromUrl) ? fromUrl : DEFAULT_PER_PAGE;
}

// Margem camel→snake do service (FC1 §A.7): a página fala snake por causa da
// URL, o service recebe camel.
function toServiceArgs(values) {
   return {
      startDate: values.start_date,
      endDate: values.end_date,
      id: values.id,
      docNumber: values.doc_number,
      pdvId: values.pdv_id,
      userId: values.user_id,
      moveType: values.move_type,
      movementFlag: values.movement_flag,
      prodSku: values.prod_sku,
   };
}

// CSV para Excel pt-BR: separador `;` e BOM (sem ele o Excel lê UTF-8 como
// latin-1 e quebra os acentos).
function downloadCsv(rows, filename) {
   // Mesmo recorte da tela. Itens e unidades vão em COLUNAS SEPARADAS e
   // numéricas (na tabela são um rótulo só, "3 itens · 9 un.", que numa
   // planilha não soma nem filtra).
   const header = [
      'ID',
      'PDV',
      'Usuário',
      'Tipo',
      'Documento',
      'Nº documento',
      'Data/hora',
      'Itens',
      'Unidades',
      'Situação',
   ];

   const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

   const body = rows.map((movement) => {
      const { date, time } = movementDateParts(movement?.created_at);
      return [
         movement?.id,
         movementPdvLabel(movement),
         movementUserLabel(movement),
         moveTypeLabel(movement?.move_type),
         movement?.doc_type ?? '',
         movement?.doc_number ?? '',
         [date, time].filter(Boolean).join(' '),
         movement?.items_count ?? '',
         movement?.units_total ?? '',
         movementFlagLabel(movement?.movement_flag),
      ]
         .map(escape)
         .join(';');
   });

   // BOM escapado (\uFEFF) de propósito: o caractere literal é invisível no
   // fonte e o lint o rejeita como whitespace irregular.
   const csv = `\uFEFF${[header.map(escape).join(';'), ...body].join('\r\n')}`;
   const url = URL.createObjectURL(
      new Blob([csv], { type: 'text/csv;charset=utf-8;' })
   );

   const link = document.createElement('a');
   link.href = url;
   link.download = filename;
   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);
   URL.revokeObjectURL(url);
}

export default function Movements() {
   const navigate = useNavigate();
   const { pdvs, isLoading: loadingPdvs } = useUserPdvs();
   const { hasPermission } = usePermissions();
   const canCreate = hasPermission('inventory:movement:create');

   const [searchParams, setSearchParams] = useSearchParams();
   // A URL é a fonte da verdade dos filtros: se vier com query params
   // (deep-link, F5, voltar-do-detalhe), restauramos e buscamos no mount; URL
   // limpa = abertura fria = NÃO busca. Leitura única (getters do useState).
   const hasInitialQueryParams = searchParams.toString() !== '';

   const defaultFiltersRef = useRef(buildDefaultFilters());

   // `filters` é o rascunho (ligado aos inputs); `appliedFilters` é o último
   // efetivamente pesquisado — paginação/ordenação/chips usam o aplicado.
   const [filters, setFilters] = useState(() =>
      readFiltersFromUrl(searchParams, defaultFiltersRef.current)
   );
   const [appliedFilters, setAppliedFilters] = useState(filters);
   const [sortConfig, setSortConfig] = useState(() =>
      readSortFromUrl(searchParams)
   );
   const [perPage, setPerPage] = useState(() =>
      readPerPageFromUrl(searchParams)
   );
   const [currentPage, setCurrentPage] = useState(() => {
      const fromUrl = Number(searchParams.get('page'));
      return Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : 1;
   });

   const [movements, setMovements] = useState([]);
   const [isLoading, setIsLoading] = useState(false);
   // Separa os dois vazios: abertura fria (convite a pesquisar) × busca sem
   // resultado (limpar filtros).
   const [hasSearched, setHasSearched] = useState(hasInitialQueryParams);

   // Rascunho do autocomplete de produto: o texto visível. Restaurado da URL
   // como o próprio SKU (7 dígitos) — a resolução texto→prod_sku do submit
   // re-deriva sozinha; não é preciso re-hidratar o objeto selecionado.
   const [productQuery, setProductQuery] = useState(
      () => filters.prod_sku || ''
   );
   const selectedProductRef = useRef(null);
   const userSearchRef = useRef(null);
   // Rótulo do usuário escolhido, só para o chip ("Usuário: 12 - Camila").
   // Restaurado da URL vira o id cru — é o que temos sem um GET /users/:id.
   const [userLabel, setUserLabel] = useState(() =>
      filters.user_id ? `#${filters.user_id}` : ''
   );

   const didInitialFetch = useRef(false);

   const periodKey = derivePeriodKey(filters.start_date, filters.end_date);

   const updateField = (key, value) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
   };

   const handleProductSelect = (selectedProductData) => {
      selectedProductRef.current = selectedProductData; // síncrono (submit no mesmo tick)
   };

   // ─── Serializa filtros + ordenação + página para a URL (sempre replace,
   // navegacao.md 1.6 — paginar/filtrar não empilha histórico). `applied=1` é
   // o sentinela de "houve busca" (1.5). ────────────────────────────────────
   const commitToUrl = useCallback(
      (values, sort, page, rowsPerPage) => {
         const params = { applied: '1' };
         Object.keys(defaultFiltersRef.current).forEach((key) => {
            if (values[key]) params[key] = values[key];
         });
         if (sort.key) {
            params.order_by = sort.key;
            params.order_dir = sort.direction;
         }
         if (page > 1) params.page = String(page);
         if (rowsPerPage !== DEFAULT_PER_PAGE) {
            params.per_page = String(rowsPerPage);
         }
         setSearchParams(params, { replace: true });
      },
      [setSearchParams]
   );

   // ─── Busca na API. NÃO reseta página/ordenação (quem reseta é o submit) —
   // assim o fetch do mount preserva o page/sort restaurados da URL. ─────────
   const fetchMovements = useCallback(async (values) => {
      setIsLoading(true);
      setHasSearched(true);
      try {
         const rows = await searchMovements(toServiceArgs(values));
         setMovements(rows);
      } catch (error) {
         notifyApiError(error, { fallback: 'Erro ao buscar movimentos.' });
      } finally {
         setIsLoading(false);
      }
   }, []);

   // ─── Busca inicial — só quando a URL traz filtros (deep-link / F5 /
   // voltar-do-detalhe). URL limpa não dispara busca automática (1.4). ───────
   useEffect(() => {
      if (didInitialFetch.current) return;
      didInitialFetch.current = true;
      if (!hasInitialQueryParams) return;
      fetchMovements(appliedFilters);
   }, [fetchMovements, hasInitialQueryParams, appliedFilters]);

   // Resolve o prod_sku do rascunho: seleção do dropdown (novo formato .sku,
   // legado .prod_sku) ou SKU completo digitado à mão (7 dígitos).
   const resolveProdSku = () => {
      const resolved = selectedProductRef.current;
      const sku = resolved?.sku ?? resolved?.prod_sku ?? null;
      if (sku) return String(sku);

      const pq = (productQuery || '').trim();
      if (/^\d{7}$/.test(pq)) return pq;
      return '';
   };

   const runSearch = (values) => {
      if (!values.start_date || !values.end_date) {
         toast.error('Informe um intervalo de datas');
         return;
      }
      if (values.start_date > values.end_date) {
         toast.error('A data de início não pode ser maior que a data final.');
         return;
      }

      // Padrão BillingCharges: nova busca por filtros zera a ordenação (1.9).
      const resetSort = { key: null, direction: null };

      setFilters(values);
      setAppliedFilters(values);
      setSortConfig(resetSort);
      setCurrentPage(1);
      commitToUrl(values, resetSort, 1, perPage);
      fetchMovements(values);
   };

   // ─── Pesquisa explícita (botão Pesquisar / Enter) ─────────────────────────
   const handleSearch = (event) => {
      event.preventDefault();
      runSearch({ ...filters, prod_sku: resolveProdSku() });
   };

   // Limpa tudo e esvazia tela/URL — SEM disparar busca (1.7). Datas voltam
   // pro default (hoje), nunca pra vazio.
   const handleClearFilters = () => {
      const defaults = buildDefaultFilters();
      defaultFiltersRef.current = defaults;

      setFilters(defaults);
      setAppliedFilters(defaults);
      setProductQuery('');
      selectedProductRef.current = null;
      userSearchRef.current?.clear();
      setUserLabel('');

      setMovements([]);
      setHasSearched(false);
      setCurrentPage(1);
      setSortConfig({ key: null, direction: null });
      setSearchParams({}, { replace: true });
   };

   // Atalho de período: só preenche as datas do RASCUNHO (a busca segue
   // explícita, 1.2).
   const handlePeriodShortcut = (days) => {
      setFilters((prev) => ({
         ...prev,
         start_date: getIsoDaysAgo(days - 1),
         end_date: getTodayIso(),
      }));
   };

   // Remover chip = refazer a busca sem aquele filtro (ação explícita do
   // operador). Mantém rascunho e aplicado em sincronia.
   const handleRemoveFilter = (key) => {
      if (key === 'prod_sku') {
         setProductQuery('');
         selectedProductRef.current = null;
      }
      if (key === 'user_id') {
         userSearchRef.current?.clear();
         setUserLabel('');
      }
      runSearch({ ...appliedFilters, [key]: '' });
   };

   const handleRowOpen = (id) => {
      // SPA de verdade: preserva a entrada de histórico da lista (com a URL
      // filtrada) pro navigate(-1) do detalhe restaurar o lugar exato.
      navigate(`/movements/${id}`);
   };

   // ─── Ordenação client-side: cicla asc → desc → padrão, volta pra página 1
   // e grava na URL. Sem refetch — o sort roda em memória. ──────────────────
   const handleSort = (key) => {
      if (!SORTABLE_KEYS.has(key)) return;

      let next;
      if (sortConfig.key === key) {
         next =
            sortConfig.direction === 'asc'
               ? { key, direction: 'desc' }
               : { key: null, direction: null }; // volta à ordem do backend
      } else {
         next = { key, direction: 'asc' };
      }

      setSortConfig(next);
      setCurrentPage(1);
      commitToUrl(appliedFilters, next, 1, perPage);
   };

   const getSortIcon = (columnKey) => {
      if (sortConfig.key !== columnKey) return ' ⇅';
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
   };

   const sortableThProps = (key, title, extraClass = '') => ({
      className: `${extraClass} sortable${
         sortConfig.key === key ? ' sort-active' : ''
      }`.trim(),
      onClick: () => handleSort(key),
      title,
   });

   // Valor comparável por coluna. `created_at` e `doc_number` precisam de
   // tratamento próprio: a data chega formatada em pt-BR (comparar string
   // ordenaria pelo DIA do mês) e o nº documento é numérico.
   const sortValueOf = (movement, key) => {
      switch (key) {
         case 'pdv':
            return movementPdvLabel(movement);
         case 'user':
            return movementUserLabel(movement);
         case 'move_type':
            return moveTypeLabel(movement?.move_type);
         case 'movement_flag':
            return movementFlagLabel(movement?.movement_flag);
         case 'created_at':
            return movementDateSortValue(movement?.created_at);
         case 'doc_number':
            return Number(movement?.doc_number) || 0;
         case 'id':
            return Number(movement?.id) || 0;
         default:
            return movement?.[key];
      }
   };

   const sortedMovements = useMemo(() => {
      if (!sortConfig.key || !sortConfig.direction) return movements;

      const factor = sortConfig.direction === 'asc' ? 1 : -1;

      return [...movements].sort((a, b) => {
         const aValue = sortValueOf(a, sortConfig.key);
         const bValue = sortValueOf(b, sortConfig.key);

         if (aValue === null || aValue === undefined) return 1;
         if (bValue === null || bValue === undefined) return -1;

         if (typeof aValue === 'number' && typeof bValue === 'number') {
            return (aValue - bValue) * factor;
         }

         return (
            String(aValue).localeCompare(String(bValue), 'pt-BR', {
               sensitivity: 'base',
            }) * factor
         );
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [movements, sortConfig]);

   const totalPages = useMemo(
      () => Math.ceil(sortedMovements.length / perPage),
      [sortedMovements.length, perPage]
   );

   const currentMovements = useMemo(() => {
      const indexOfLast = currentPage * perPage;
      return sortedMovements.slice(indexOfLast - perPage, indexOfLast);
   }, [sortedMovements, currentPage, perPage]);

   // Contadores do subtítulo (sobre TODO o resultado do filtro, não a página).
   const counters = useMemo(
      () => ({
         total: movements.length,
         in: movements.filter((movement) => movement?.move_type === 'in')
            .length,
         out: movements.filter((movement) => movement?.move_type === 'out')
            .length,
      }),
      [movements]
   );

   // Paginação client-side: só muda a fatia em memória + grava na URL.
   const handlePageChange = (pageNumber) => {
      if (!totalPages) return;
      const next = Math.min(Math.max(1, pageNumber), totalPages);
      setCurrentPage(next);
      commitToUrl(appliedFilters, sortConfig, next, perPage);
   };

   const handlePerPageChange = (value) => {
      const next = Number(value);
      setPerPage(next);
      setCurrentPage(1);
      commitToUrl(appliedFilters, sortConfig, 1, next);
   };

   const handleExport = () => {
      if (!sortedMovements.length) return;
      downloadCsv(sortedMovements, `movimentos-${getTodayIso()}.csv`);
   };

   // Chips do que está APLICADO. Datas ficam fora de propósito: o intervalo é
   // obrigatório (o backend exige) e já está visível nos próprios campos.
   const activeChips = useMemo(() => {
      const chips = [];
      const pdvName = pdvs.find(
         (pdv) => String(pdv.id) === String(appliedFilters.pdv_id)
      )?.pdv_name;

      if (appliedFilters.move_type) {
         chips.push({
            key: 'move_type',
            label: appliedFilters.move_type === 'in' ? 'Entradas' : 'Saídas',
         });
      }
      if (appliedFilters.movement_flag) {
         chips.push({
            key: 'movement_flag',
            label: `Situação: ${
               STATUS_FILTER_LABEL[appliedFilters.movement_flag] ??
               appliedFilters.movement_flag
            }`,
         });
      }
      if (appliedFilters.pdv_id) {
         chips.push({
            key: 'pdv_id',
            label: `PDV: ${pdvName ?? appliedFilters.pdv_id}`,
         });
      }
      if (appliedFilters.id) {
         chips.push({ key: 'id', label: `ID: ${appliedFilters.id}` });
      }
      if (appliedFilters.doc_number) {
         chips.push({
            key: 'doc_number',
            label: `Nº doc: ${appliedFilters.doc_number}`,
         });
      }
      if (appliedFilters.user_id) {
         chips.push({
            key: 'user_id',
            label: `Usuário: ${userLabel || appliedFilters.user_id}`,
         });
      }
      if (appliedFilters.prod_sku) {
         chips.push({
            key: 'prod_sku',
            label: `Produto: ${appliedFilters.prod_sku}`,
         });
      }
      return chips;
   }, [appliedFilters, pdvs, userLabel]);

   const hasResults = currentMovements.length > 0;

   return (
      <Container>
         <Loading isLoading={isLoading || loadingPdvs} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Movimentos</PageTitle>
               <PageSubtitle>
                  {hasSearched && counters.total
                     ? `${counters.total} movimento(s) no filtro atual · ${counters.in} entrada(s) · ${counters.out} saída(s)`
                     : 'Pesquise movimentos de entrada e saída de estoque. Duplo clique na linha abre o movimento.'}
               </PageSubtitle>
            </PageHeaderText>

            {/* As duas em GHOST, de propósito (decisão do Caio, 2026-09-12).
                O protótipo dava peso diferente às duas (saída secundária,
                entrada primária) e o kit on-dark traduziria isso em
                ghost + CTA sólido — mas aqui são ações IRMÃS: criar movimento,
                mudando só a direção. Destacar uma afirmaria que entrada importa
                mais que saída, afirmação sobre a operação, não sobre design.
                (Em Transferências o par ghost+CTA é legítimo: lá as ações são
                de classes distintas — consultar outra fila × criar.) */}
            {canCreate && (
               <PageHeaderActions>
                  <GhostOnDarkButton as={Link} to="/movement-out">
                     <FaArrowUp size={11} aria-hidden="true" /> Nova saída
                  </GhostOnDarkButton>
                  <GhostOnDarkButton as={Link} to="/movement-in">
                     <FaArrowDown size={11} aria-hidden="true" /> Nova entrada
                  </GhostOnDarkButton>
               </PageHeaderActions>
            )}
         </PageHeader>

         <Section>
            <SectionHeader>
               <FilterHeadRow>
                  <SectionTitle style={{ margin: 0 }}>Pesquisa</SectionTitle>
                  <ShortcutsGroup>
                     <span className="lbl">Atalhos de período</span>
                     <Segmented>
                        {PERIOD_SHORTCUTS.map((shortcut) => (
                           <button
                              key={shortcut.key}
                              type="button"
                              className={
                                 periodKey === shortcut.key ? 'active' : ''
                              }
                              onClick={() =>
                                 handlePeriodShortcut(shortcut.days)
                              }
                              disabled={isLoading}
                           >
                              {shortcut.label}
                           </button>
                        ))}
                     </Segmented>
                  </ShortcutsGroup>
               </FilterHeadRow>
            </SectionHeader>

            <SectionBody>
               <form onSubmit={handleSearch}>
                  <FormGrid $cols={12} $colsMobile={12}>
                     <Field $col={2} $colMobile={6}>
                        <label htmlFor="filter-start-date">Data inicial</label>
                        <input
                           id="filter-start-date"
                           type="date"
                           value={filters.start_date}
                           onChange={(event) =>
                              updateField('start_date', event.target.value)
                           }
                        />
                     </Field>

                     <Field $col={2} $colMobile={6}>
                        <label htmlFor="filter-end-date">Data final</label>
                        <input
                           id="filter-end-date"
                           type="date"
                           value={filters.end_date}
                           onChange={(event) =>
                              updateField('end_date', event.target.value)
                           }
                        />
                     </Field>

                     <Field $col={2} $colMobile={6}>
                        <label htmlFor="filter-id">ID do movimento</label>
                        <input
                           id="filter-id"
                           type="text"
                           value={filters.id}
                           onChange={(event) =>
                              updateField('id', event.target.value)
                           }
                           placeholder="Ex.: 500"
                        />
                     </Field>

                     <Field $col={2} $colMobile={6}>
                        <label htmlFor="filter-doc-number">
                           Nº do documento
                        </label>
                        <input
                           id="filter-doc-number"
                           type="text"
                           value={filters.doc_number}
                           onChange={(event) =>
                              updateField('doc_number', event.target.value)
                           }
                           placeholder="Ex.: 123"
                        />
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label htmlFor="filter-pdv">PDV</label>
                        <select
                           id="filter-pdv"
                           value={filters.pdv_id}
                           onChange={(event) =>
                              updateField('pdv_id', event.target.value)
                           }
                           disabled={isLoading || loadingPdvs}
                        >
                           <option value="">Todos os PDVs</option>
                           {pdvs.map((pdv) => (
                              <option key={pdv.id} value={pdv.id}>
                                 {pdv.pdv_name}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label>Usuário</label>
                        <UserNameSearch
                           ref={userSearchRef}
                           value={filters.user_id}
                           onChange={(nextId, user) => {
                              updateField('user_id', nextId);
                              // Mesmo template do dropdown (inclusive a marca
                              // "(inativo)" de quem saiu da empresa).
                              setUserLabel(userOptionLabel(user));
                           }}
                           disabled={isLoading}
                           placeholder="Digite 3+ letras para sugerir..."
                        />
                     </Field>

                     <Field $col={8} $colMobile={12}>
                        <label>Produto (SKU ou nome)</label>
                        <ProductNameSearch
                           value={productQuery}
                           onChange={setProductQuery}
                           onSelect={handleProductSelect}
                           nameLike={false} // modo restrito da listagem
                           displayMode="sku-name"
                           placeholder="Digite o SKU (7 dígitos) ou busque pelo nome..."
                           showHint
                        />
                     </Field>

                     <Field $col={6} $colMobile={12}>
                        <label>Tipo</label>
                        <Segmented>
                           {MOVE_TYPE_SEGMENTS.map((segment) => (
                              <button
                                 key={segment.value || 'all'}
                                 type="button"
                                 className={
                                    filters.move_type === segment.value
                                       ? 'active'
                                       : ''
                                 }
                                 onClick={() =>
                                    updateField('move_type', segment.value)
                                 }
                                 disabled={isLoading}
                              >
                                 {segment.label}
                              </button>
                           ))}
                        </Segmented>
                     </Field>

                     <Field $col={6} $colMobile={12}>
                        <label>Situação</label>
                        <Segmented>
                           {STATUS_SEGMENTS.map((segment) => (
                              <button
                                 key={segment.value || 'all'}
                                 type="button"
                                 className={
                                    filters.movement_flag === segment.value
                                       ? 'active'
                                       : ''
                                 }
                                 onClick={() =>
                                    updateField('movement_flag', segment.value)
                                 }
                                 disabled={isLoading}
                              >
                                 {segment.label}
                              </button>
                           ))}
                        </Segmented>
                     </Field>
                  </FormGrid>

                  <FilterFoot>
                     <ChipList>
                        {activeChips.map((chip) => (
                           <Chip key={chip.key}>
                              {chip.label}
                              <button
                                 type="button"
                                 onClick={() => handleRemoveFilter(chip.key)}
                                 disabled={isLoading}
                                 title={`Remover filtro ${chip.label}`}
                                 aria-label={`Remover filtro ${chip.label}`}
                              >
                                 ×
                              </button>
                           </Chip>
                        ))}
                     </ChipList>

                     <div className="acts">
                        <GhostButton
                           type="button"
                           onClick={handleClearFilters}
                           disabled={isLoading}
                        >
                           Limpar
                        </GhostButton>
                        <CtaButton type="submit" disabled={isLoading}>
                           <FaSearch size={12} aria-hidden="true" /> Pesquisar
                        </CtaButton>
                     </div>
                  </FilterFoot>
               </form>
            </SectionBody>
         </Section>

         <Section>
            <SectionHeader>
               <SectionHeaderRow>
                  <HeadLeft>
                     <SectionTitle style={{ margin: 0 }}>
                        Resultados
                     </SectionTitle>

                     <SectionMeta
                        items={[
                           {
                              key: 'count',
                              label: 'Movimentos:',
                              value: counters.total,
                           },
                           {
                              key: 'page',
                              label: 'Página',
                              value: `${currentPage}/${totalPages || 1}`,
                           },
                        ]}
                     />
                  </HeadLeft>

                  <ResultsTools>
                     <label htmlFor="results-per-page">
                        Linhas
                        <select
                           id="results-per-page"
                           value={perPage}
                           onChange={(event) =>
                              handlePerPageChange(event.target.value)
                           }
                           disabled={isLoading}
                        >
                           {PER_PAGE_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                 {option}
                              </option>
                           ))}
                        </select>
                     </label>

                     <SmallButton
                        type="button"
                        onClick={handleExport}
                        disabled={isLoading || !sortedMovements.length}
                        title="Exportar o resultado do filtro em CSV"
                     >
                        <FaDownload size={11} aria-hidden="true" /> Exportar
                     </SmallButton>
                  </ResultsTools>
               </SectionHeaderRow>
            </SectionHeader>

            <SectionBody>
               {hasResults && (
                  <>
                     <TableWrapper $minWidth="1060px">
                        <DataTable>
                           {/* Ordem das colunas ratificada pelo Caio em
                               2026-09-12: ID · PDV · Tipo · Data · Documento ·
                               Itens · Situação · Usuário (o usuário fecha a
                               linha — é o dado de menor consulta). */}
                           <thead>
                              <tr>
                                 <th
                                    {...sortableThProps(
                                       'id',
                                       'Ordenar por ID',
                                       'center'
                                    )}
                                 >
                                    ID{getSortIcon('id')}
                                 </th>
                                 <th {...sortableThProps('pdv', 'Ordenar por PDV')}>
                                    PDV{getSortIcon('pdv')}
                                 </th>
                                 <th
                                    {...sortableThProps(
                                       'move_type',
                                       'Ordenar por tipo',
                                       'center'
                                    )}
                                 >
                                    Tipo{getSortIcon('move_type')}
                                 </th>
                                 <th
                                    {...sortableThProps(
                                       'created_at',
                                       'Ordenar por data'
                                    )}
                                 >
                                    Data{getSortIcon('created_at')}
                                 </th>
                                 <th
                                    {...sortableThProps(
                                       'doc_type',
                                       'Ordenar por tipo de documento'
                                    )}
                                 >
                                    Documento{getSortIcon('doc_type')}
                                 </th>
                                 {/* Agregados do handoff 0109. Fora de
                                     SORTABLE_KEYS de propósito: a ordenação
                                     desta tela é client-side, e ordenar por
                                     agregado só faz sentido quando ela for do
                                     servidor (decisão registrada na spec). */}
                                 <th
                                    className="center"
                                    title="Linhas de item e soma das quantidades do movimento"
                                 >
                                    Itens
                                 </th>
                                 <th
                                    {...sortableThProps(
                                       'movement_flag',
                                       'Ordenar por situação',
                                       'center'
                                    )}
                                 >
                                    Situação{getSortIcon('movement_flag')}
                                 </th>
                                 <th
                                    {...sortableThProps(
                                       'user',
                                       'Ordenar por usuário'
                                    )}
                                 >
                                    Usuário{getSortIcon('user')}
                                 </th>
                              </tr>
                           </thead>
                           <tbody>
                              {currentMovements.map((movement) => {
                                 const { date, time } = movementDateParts(
                                    movement?.created_at
                                 );
                                 return (
                                    <ClickableRow
                                       key={String(movement.id)}
                                       onDoubleClick={() =>
                                          handleRowOpen(movement.id)
                                       }
                                       title="Duplo clique para abrir o movimento"
                                    >
                                       <td className="center">{movement.id}</td>
                                       <td>{movementPdvLabel(movement)}</td>
                                       <td className="center">
                                          <StatusBadge
                                             size="sm"
                                             variant={moveTypeVariant(
                                                movement?.move_type
                                             )}
                                          >
                                             {moveTypeLabel(
                                                movement?.move_type
                                             ) || '—'}
                                          </StatusBadge>
                                       </td>
                                       <td className="nowrap">
                                          <TwoLine>
                                             <span>{date || '—'}</span>
                                             {time ? (
                                                <span className="sub">
                                                   {time}
                                                </span>
                                             ) : null}
                                          </TwoLine>
                                       </td>
                                       <td>
                                          <TwoLine>
                                             <span>
                                                {movement?.doc_type ?? '—'}
                                             </span>
                                             {/* Movimento de transferência: o
                                                 nº do documento repete o nº da
                                                 ordem, que a tag já mostra —
                                                 duas linhas dizendo o mesmo.
                                                 Fica só a tag (2026-09-12). */}
                                             {movement?.transfer ? (
                                                <TransferTag
                                                   title={`Gerado pela transferência #${movement.transfer.transfer_order_id} — só muda pelo fluxo de transferência`}
                                                >
                                                   TRANSF. #
                                                   {
                                                      movement.transfer
                                                         .transfer_order_id
                                                   }
                                                </TransferTag>
                                             ) : (
                                                movement?.doc_number && (
                                                   <span className="sub">
                                                      nº {movement.doc_number}
                                                   </span>
                                                )
                                             )}
                                          </TwoLine>
                                       </td>
                                       <td className="center nowrap">
                                          {formatItemsSummary(
                                             movement?.items_count,
                                             movement?.units_total
                                          ) || '—'}
                                       </td>
                                       <td className="center">
                                          <StatusBadge
                                             size="sm"
                                             variant={movementFlagVariant(
                                                movement?.movement_flag
                                             )}
                                          >
                                             {movementFlagLabel(
                                                movement?.movement_flag
                                             ) || '—'}
                                          </StatusBadge>
                                       </td>
                                       <td>{movementUserLabel(movement)}</td>
                                    </ClickableRow>
                                 );
                              })}
                           </tbody>
                        </DataTable>
                     </TableWrapper>

                     <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        disabled={isLoading}
                        note={`Página ${currentPage} de ${totalPages || 1} · ${perPage} por página`}
                     />
                  </>
               )}

               {!hasResults && !hasSearched && (
                  <EmptyState
                     icon={<FaBoxOpen size={20} />}
                     title="Pesquise para começar"
                     description="Escolha o período e os filtros e clique em Pesquisar — ou use um atalho de período."
                  />
               )}

               {!hasResults && hasSearched && !isLoading && (
                  <EmptyState
                     icon={<FaSearch size={18} />}
                     title="Nenhum movimento no filtro atual"
                     description="Amplie o período ou limpe os filtros."
                     action={
                        <GhostButton type="button" onClick={handleClearFilters}>
                           Limpar filtros
                        </GhostButton>
                     }
                  />
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
